/**
 * Utility functions for parsing and displaying SAS token information
 */

export interface SasPermissions {
  read: boolean;
  add: boolean;
  create: boolean;
  write: boolean;
  delete: boolean;
  list: boolean;
}

export interface SasTokenInfo {
  permissions: SasPermissions;
  status: 'valid' | 'invalid' | 'expired';
  startTime?: Date;
  expiryTime?: Date;
  protocol?: string;
  version?: string;
  resource: string;
  isValid: boolean;
  isExpired: boolean;
  message?: string; 
}

/**
 * Parse SAS token permissions from URL
 */
export const parseSasToken = (sasUrl: string): SasTokenInfo | null => {
  try {
    const url = new URL(sasUrl);
    const params = new URLSearchParams(url.search);

    // Extract permissions
    const sp = params.get('sp') || '';
    const permissions: SasPermissions = {
      read: sp.includes('r'),
      add: sp.includes('a'),
      create: sp.includes('c'),
      write: sp.includes('w'),
      delete: sp.includes('d'),
      list: sp.includes('l'),
    };

    // Extract dates
    const startTime = params.get('st') ? new Date(params.get('st')!) : undefined;
    const expiryTime = params.get('se') ? new Date(params.get('se')!) : undefined;

    // Extract other info
    const protocol = params.get('spr') || undefined;
    const version = params.get('sv') || undefined;
    const resource = params.get('sr') || '';

    // Check if token is valid and not expired
    const now = new Date();
    const isExpired = expiryTime ? expiryTime < now : false;
    const isValid = params.has('sig') && params.has('sp') && !isExpired;

    return {
      status: isExpired ? 'expired' : (isValid ? 'valid' : 'invalid'),
      permissions,
      startTime,
      expiryTime,
      protocol,
      version,
      resource,
      isValid,
      isExpired,
    };
  } catch (error) {
    return null;
  }
};

/**
 * Get permission names as readable strings
 */
export const getPermissionNames = (permissions: SasPermissions): string[] => {
  const names: string[] = [];
  if (permissions.read) names.push('Lesen');
  if (permissions.add) names.push('Hinzufügen');
  if (permissions.create) names.push('Erstellen');
  if (permissions.write) names.push('Schreiben');
  if (permissions.delete) names.push('Löschen');
  if (permissions.list) names.push('Auflisten');
  return names;
};

/**
 * Get resource type as readable string
 */
export const getResourceType = (resource: string): string => {
  switch (resource) {
    case 'c': return 'Container';
    case 'b': return 'Blob';
    case 's': return 'Service';
    default: return 'Unkown';
  }
};

/**
 * Format date for display
 */
export const formatSasDate = (date: Date): string => {
  return date.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};